import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { ref, getDownloadURL, listAll } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db, storage, app } from '../config/firebase';
import Sidebar from '../components/Sidebar/Sidebar';
import { useAdminRole } from '../hooks/useAdminRole';
import './RevenueUploadPage.css';

const RevenueUploadPage = () => {
  const navigate = useNavigate();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentFile, setCurrentFile] = useState(null);
  const { role: userRole } = useAdminRole();

  // Vérifier l'état d'authentification et le rôle (seul role === 0 peut accéder)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setLoading(false);
        navigate('/admin');
        return;
      }

      try {
        const userDocRef = doc(db, 'adminUsers', currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        const role = userDocSnap.exists() ? (userDocSnap.data().role ?? 1) : 1;

        if (role !== 0) {
          navigate('/admin');
          return;
        }
        
        // Charger les informations du fichier actuel
        loadCurrentFile();
      } catch (error) {
        console.error('Erreur lors du chargement du rôle:', error);
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Charger les informations du fichier actuel (merged_revenue.csv)
  const loadCurrentFile = async () => {
    // Vérifier que l'utilisateur est authentifié
    if (!auth.currentUser) {
      console.warn('Utilisateur non authentifié, impossible de charger le fichier');
      setCurrentFile(null);
      return;
    }

    try {
      const revenueFilesRef = ref(storage, 'revenue_file/');
      const filesList = await listAll(revenueFilesRef);
      
      // Chercher merged_revenue.csv en priorité, sinon prendre le premier fichier
      let fileRef = null;
      const mergedFile = filesList.items.find(item => item.name === 'merged_revenue.csv');
      if (mergedFile) {
        fileRef = mergedFile;
      } else if (filesList.items.length > 0) {
        fileRef = filesList.items[0];
      }
      
      if (fileRef) {
        const url = await getDownloadURL(fileRef);
        setCurrentFile({
          name: fileRef.name,
          url: url,
          fullPath: fileRef.fullPath
        });
      } else {
        setCurrentFile(null);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du fichier actuel:', error);
      // Si c'est une erreur de permission, afficher un message plus clair
      if (error.code === 'storage/unauthorized') {
        setError('Vous n\'avez pas les permissions nécessaires pour accéder aux fichiers. Veuillez vous reconnecter.');
      }
      setCurrentFile(null);
    }
  };

  // Gérer l'upload du fichier et la concaténation
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Vérifier que c'est un fichier CSV
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Veuillez sélectionner un fichier CSV');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    try {
      // Lire le contenu du fichier
      const csvContent = await file.text();
      
      // Simuler la progression
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 80) {
            clearInterval(progressInterval);
            return 80;
          }
          return prev + 10;
        });
      }, 200);

      // Appeler la fonction de concaténation
      const functions = getFunctions(app);
      const mergeCSV = httpsCallable(functions, 'mergeRevenueCSV');
      
      setUploadProgress(85);
      const result = await mergeCSV({
        csvContent: csvContent,
        fileName: file.name
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = result.data;
      if (data.success) {
        // Recharger le fichier fusionné
        await loadCurrentFile();
        
        const dateRange = data.dateRange;
        const dateRangeStr = dateRange.min && dateRange.max 
          ? ` (${dateRange.min} - ${dateRange.max})`
          : '';
        
        setSuccess(
          `Fichier "${file.name}" fusionné avec succès ! ` +
          `${data.totalTickets} tickets uniques${dateRangeStr}. ` +
          `${data.duplicatesRemoved} doublons supprimés.`
        );
      } else {
        throw new Error(data.message || 'Erreur lors de la fusion');
      }
      
      // Réinitialiser le champ de fichier
      event.target.value = '';
      
      // Effacer le message de succès après 8 secondes
      setTimeout(() => {
        setSuccess('');
      }, 8000);
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      setError('Erreur lors de la fusion du fichier: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return (
      <div className="revenue-upload-page">
        <div className="loading-container">
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="revenue-upload-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} userRole={userRole} />
      
      <div className="revenue-upload-content">
        <header className="revenue-upload-header">
          <button 
            className="sidebar-toggle-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Toggle sidebar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <h1>Upload Fichier Revenus</h1>
        </header>

        <main className="revenue-upload-main">
          <div className="upload-container">
            <div className="upload-card">
              <div className="upload-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              
              <h2>Uploader un fichier CSV</h2>
              <p className="upload-description">
                Sélectionnez un fichier CSV contenant les données de revenus. 
                Le fichier sera automatiquement fusionné avec les données existantes. 
                Les doublons seront supprimés et les données seront triées par date.
              </p>

              <div className="upload-input-container">
                <input
                  type="file"
                  id="csv-upload"
                  accept=".csv"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="file-input"
                />
                <label htmlFor="csv-upload" className={`file-input-label ${uploading ? 'disabled' : ''}`}>
                  {uploading ? 'Upload en cours...' : 'Choisir un fichier CSV'}
                </label>
              </div>

              {uploading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="progress-text">{uploadProgress}%</p>
                </div>
              )}

              {error && (
                <div className="message error-message">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {error}
                </div>
              )}

              {success && (
                <div className="message success-message">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {success}
                </div>
              )}
            </div>

            {currentFile && (
              <div className="current-file-card">
                <h3>Fichier actuel</h3>
                <div className="file-info">
                  <div className="file-name">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span>{currentFile.name}</span>
                  </div>
                  <a 
                    href={currentFile.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="download-link"
                  >
                    Télécharger
                  </a>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default RevenueUploadPage;

